import React, { useMemo, useState, useEffect } from "react";
import { useTable, usePagination } from "react-table";
import Pagination from "@mui/material/Pagination";

const ReactTable = ({
  columns,
  data,
  params,
  setParams,
  serverSide = false,
  totalCount,
  renderRowSubComponent,
}) => {
  const memoColumns = useMemo(() => columns, [columns]);
  const memoData = useMemo(() => data, [data]);

  // Changed from array [] to null to hold a single ID
  const [expandedRow, setExpandedRow] = useState(null);

  // Calculate page count for server-side
  const pageCount = serverSide
    ? Math.ceil((totalCount || 0) / (params?.page_size || 50))
    : undefined;

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    gotoPage,
    state: { pageIndex },
    pageOptions,
  } = useTable(
    {
      columns: memoColumns,
      data: memoData,
      initialState: { pageIndex: 0, pageSize: params?.page_size || 50 },
      manualPagination: serverSide,
      pageCount: serverSide ? pageCount : undefined,
    },
    usePagination
  );

  const totalPages = serverSide ? pageCount : pageOptions.length;
  const [gotoPageInput, setGotoPageInput] = useState("");

  // Sync server-side params
  useEffect(() => {
    if (serverSide && typeof setParams === "function") {
      setParams((prev) => ({
        ...prev,
        page: pageIndex + 1,
      }));
    }
  }, [serverSide, pageIndex, setParams]);

  const handleChange = (event, value) => {
    gotoPage(value - 1);
    setExpandedRow(null); // Optional: Close rows when changing page
  };

  const handleGotoPage = () => {
    const pageNum = Number(gotoPageInput);
    if (pageNum > 0 && pageNum <= totalPages) {
      gotoPage(pageNum - 1);
      setGotoPageInput("");
      setExpandedRow(null); // Optional: Close rows when changing page
    }
  };

  // Logic to only allow one row open at a time
  const handleRowClick = (rowId) => {
    setExpandedRow((prev) => (prev === rowId ? null : rowId));
  };

  return (
    <div className="table_wrapper" style={{ minHeight: "60vh" }}>
      <div style={{ marginTop: "20px" }} className="table_scroll">
        <table {...getTableProps()} className="leaderboard-table">
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps()} key={column.id}>
                    {column.render("Header")}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row, i) => {
              prepareRow(row);
              // Use a unique identifier if available (like row.original.id), otherwise fallback to index 'i'
              // Note: Using 'i' can cause issues if data sorting changes, prefer unique ID.
              const rowId = row.original?.id || row.id; 
              
              // Check if THIS row is the one stored in state
              const isExpanded = expandedRow === rowId;

              return (
                <React.Fragment key={rowId}>
                  <tr
                    onClick={() => handleRowClick(rowId)}
                    {...row.getRowProps()}
                    className={isExpanded ? "economiccalender-row-active" : ""}
                    style={{ cursor: renderRowSubComponent ? "pointer" : "default" }}
                  >
                    {row.cells.map((cell, j) => (
                      <td {...cell.getCellProps()} key={j}>
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>

                  {isExpanded && renderRowSubComponent && (
                    <tr className="expanded-row">
                      <td colSpan={columns.length}>
                        {renderRowSubComponent({ row })}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="spacerT30p"></div>
      <div
        className="pagination_wrapper"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "20px",
          padding: "0 16px 16px",
        }}
      >
        <Pagination
          onChange={handleChange}
          count={totalPages}
          variant="outlined"
          page={params?.page || 1}
          color="primary"
          disabled={totalPages === 0}
        />
        <div className="goto-page-input">
          <input
            type="number"
            placeholder="Go to Page"
            value={gotoPageInput}
            min={1}
            max={totalPages}
            onChange={(e) => setGotoPageInput(e.target.value)}
          />
          <button className="goToButton" onClick={handleGotoPage}>
            Go
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReactTable;